import { useCallback, useEffect, useRef, useState } from "react";
import { createInvite, fetchInvites, revokeInvite } from "./invites-api";
import { type InvitesListState, inviteLink, inviteLinkBase } from "./invites-panel";

interface UseInvitesOptions {
  /** Окно открыто: список читается при открытии, а токен живёт, пока оно открыто. */
  open: boolean;
  onSessionInvalid: () => void;
}

export interface UseInvitesResult {
  state: InvitesListState;
  /** Ссылка только что выданного приглашения; пропадает вместе с окном (токен демон не хранит). */
  createdLink: string | null;
  creating: boolean;
  pendingIds: ReadonlySet<string>;
  failure: string | null;
  create: () => Promise<void>;
  revoke: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

const CREATE_FAILED = "Ссылку создать не удалось — попробуйте ещё раз.";
const CREATE_FORBIDDEN = "Создать ссылку нельзя: права нет, либо приглашения выключены.";
const REVOKE_FAILED = "Ссылку отозвать не удалось — попробуйте ещё раз.";
const REVOKE_CONFLICT = "Ссылка уже использована, истекла или отозвана — список обновлён.";
const REVOKE_NOT_FOUND = "Такой ссылки нет — список обновлён.";

/**
 * Приглашения §104 п. 6: список, выдача, отзыв. Список перечитывается у
 * демона после каждого действия — состав и статусы считает он, клиент их не
 * выводит. Окно закрыто — состояние сброшено, в том числе выданный токен.
 */
export function useInvites({ open, onSessionInvalid }: UseInvitesOptions): UseInvitesResult {
  const [state, setState] = useState<InvitesListState>({ status: "loading" });
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [failure, setFailure] = useState<string | null>(null);
  const onSessionInvalidRef = useRef(onSessionInvalid);
  onSessionInvalidRef.current = onSessionInvalid;
  // Поколение окна: ответ, пришедший после закрытия, в состояние не ложится.
  const generation = useRef(0);

  const load = useCallback(async () => {
    const mine = generation.current;
    const result = await fetchInvites();
    if (mine !== generation.current) {
      return;
    }
    switch (result.status) {
      case "ok":
        setState({ status: "ready", invites: result.invites });
        return;
      case "unauthorized":
        onSessionInvalidRef.current();
        return;
      case "forbidden":
        setState({ status: "forbidden" });
        return;
      case "unavailable":
        // Неудача поверх прочитанного списка его не гасит.
        setState((previous) =>
          previous.status === "ready" ? previous : { status: "unavailable" },
        );
        return;
      default:
        result satisfies never;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      generation.current += 1;
      setState({ status: "loading" });
      setCreatedLink(null);
      setCreating(false);
      setPendingIds(new Set());
      setFailure(null);
      return;
    }
    void load();
  }, [open, load]);

  const create = useCallback(async () => {
    const mine = generation.current;
    setCreating(true);
    setFailure(null);
    const result = await createInvite();
    if (mine !== generation.current) {
      return;
    }
    setCreating(false);
    switch (result.status) {
      case "ok":
        setCreatedLink(inviteLink(inviteLinkBase(window.location), result.created.token));
        await load();
        return;
      case "unauthorized":
        onSessionInvalidRef.current();
        return;
      case "forbidden":
        setFailure(CREATE_FORBIDDEN);
        return;
      case "unavailable":
        setFailure(CREATE_FAILED);
        return;
      default:
        result satisfies never;
    }
  }, [load]);

  const revoke = useCallback(
    async (id: string) => {
      const mine = generation.current;
      setFailure(null);
      setPendingIds((previous) => new Set(previous).add(id));
      const result = await revokeInvite(id);
      if (mine !== generation.current) {
        return;
      }
      setPendingIds((previous) => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
      switch (result.status) {
        case "ok":
          await load();
          return;
        case "unauthorized":
          onSessionInvalidRef.current();
          return;
        case "conflict":
          setFailure(REVOKE_CONFLICT);
          await load();
          return;
        case "not_found":
          setFailure(REVOKE_NOT_FOUND);
          await load();
          return;
        case "unavailable":
          setFailure(REVOKE_FAILED);
          return;
        default:
          result satisfies never;
      }
    },
    [load],
  );

  return { state, createdLink, creating, pendingIds, failure, create, revoke, reload: load };
}
